import JSZip from "jszip";
import { downloadBlob } from "./videoExport.js";

const LANGUAGE_EXTENSIONS = {
  javascript: "js",
  typescript: "ts",
  python: "py",
  html: "html",
  css: "css",
  json: "json",
  go: "go",
  rust: "rs",
  java: "java",
  cpp: "cpp",
  csharp: "cs",
  sql: "sql",
  shell: "sh",
  markdown: "md",
};

export function slugifyIdentifier(name) {
  const slug = String(name || "app")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .slice(0, 48);
  return slug || "app";
}

export function defaultBundleIds(appName) {
  const slug = slugifyIdentifier(appName);
  return {
    iosBundleId: `com.consensus.${slug}`,
    androidPackage: `com.consensus.${slug}`,
  };
}

/** Normalize Sandpack file maps (`/App.js` → `{ code }` or string). */
export function normalizeSandpackFiles(raw) {
  if (!raw || typeof raw !== "object") return {};
  return Object.fromEntries(
    Object.entries(raw).map(([path, file]) => {
      const code = typeof file === "string" ? file : file?.code ?? "";
      const cleanPath = path.replace(/^\//, "");
      return [cleanPath, code];
    })
  );
}

export function buildSingleFileExport({ code, language, appName }) {
  const ext = LANGUAGE_EXTENSIONS[language] || "txt";
  const slug = slugifyIdentifier(appName);
  return {
    [`${slug}.${ext}`]: code,
    "README.md": `# ${appName || "Project"}

Generated by [The Consensus A.I.](https://theconsensusai.com)

## File

\`${slug}.${ext}\` — your ${language} source file.
`,
  };
}

export function buildReactWebProjectFiles({ appName, sandpackFiles }) {
  const files = normalizeSandpackFiles(sandpackFiles);
  const appCode = files["App.js"] || files["src/App.jsx"] || "";
  const slug = slugifyIdentifier(appName);

  return {
    "index.html": `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>${escapeHtml(appName)}</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.jsx"></script>
  </body>
</html>
`,
    "src/main.jsx": `import React from "react";
import { createRoot } from "react-dom/client";
import App from "./App.jsx";

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
`,
    "src/App.jsx": appCode,
    "package.json": JSON.stringify(
      {
        name: slug,
        private: true,
        version: "1.0.0",
        type: "module",
        scripts: {
          dev: "vite",
          build: "vite build",
          preview: "vite preview",
        },
        dependencies: {
          react: "^18.3.1",
          "react-dom": "^18.3.1",
        },
        devDependencies: {
          "@vitejs/plugin-react": "^4.3.4",
          vite: "^5.4.11",
        },
      },
      null,
      2
    ),
    "vite.config.js": `import { defineConfig } from "vite";
import react from "@vitejs/plugin-react";

export default defineConfig({
  plugins: [react()],
});
`,
    "README.md": `# ${appName}

Generated by [The Consensus A.I.](https://theconsensusai.com)

## Deploy

\`\`\`bash
npm install
npm run dev      # local development
npm run build    # production build in dist/
\`\`\`

Host the \`dist/\` folder on Netlify, Vercel, Cloudflare Pages, or any static host.
`,
  };
}

function dataUrlToBase64(dataUrl) {
  const comma = String(dataUrl).indexOf(",");
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

function binaryAsset(dataUrl) {
  return { __binary: true, data: dataUrlToBase64(dataUrl) };
}

export function buildExpoProjectFiles({
  appName,
  iosBundleId,
  androidPackage,
  iconDataUrl,
  sandpackFiles,
}) {
  const files = normalizeSandpackFiles(sandpackFiles);
  const appCode = files["App.js"] || "";
  const slug = slugifyIdentifier(appName);

  return {
    "App.js": appCode,
    "app.json": JSON.stringify(
      {
        expo: {
          name: appName,
          slug,
          version: "1.0.0",
          orientation: "portrait",
          icon: "./assets/icon.png",
          userInterfaceStyle: "automatic",
          splash: {
            image: "./assets/splash.png",
            resizeMode: "contain",
            backgroundColor: "#05060a",
          },
          ios: {
            supportsTablet: true,
            bundleIdentifier: iosBundleId,
          },
          android: {
            adaptiveIcon: {
              foregroundImage: "./assets/adaptive-icon.png",
              backgroundColor: "#05060a",
            },
            package: androidPackage,
          },
          web: {
            favicon: "./assets/favicon.png",
          },
        },
      },
      null,
      2
    ),
    "package.json": JSON.stringify(
      {
        name: slug,
        version: "1.0.0",
        main: "node_modules/expo/AppEntry.js",
        private: true,
        scripts: {
          start: "expo start",
          android: "expo start --android",
          ios: "expo start --ios",
          web: "expo start --web",
        },
        dependencies: {
          expo: "~54.0.0",
          react: "19.1.0",
          "react-native": "0.81.0",
        },
      },
      null,
      2
    ),
    "babel.config.js": `module.exports = function (api) {
  api.cache(true);
  return { presets: ["babel-preset-expo"] };
};
`,
    ...(iconDataUrl
      ? {
          "assets/icon.png": binaryAsset(iconDataUrl),
          "assets/splash.png": binaryAsset(iconDataUrl),
          "assets/adaptive-icon.png": binaryAsset(iconDataUrl),
          "assets/favicon.png": binaryAsset(iconDataUrl),
        }
      : { "assets/.gitkeep": "" }),
    "README.md": `# ${appName}

Generated by [The Consensus A.I.](https://theconsensusai.com)

## Deploy with Expo (SDK 54)

\`\`\`bash
npm install
npx expo start
\`\`\`

- **iOS / Android:** scan the QR code with Expo Go (SDK 54), or run \`eas build\` for store builds.
- **Bundle IDs:** iOS \`${iosBundleId}\`, Android \`${androidPackage}\`
${iconDataUrl ? "" : "\nAdd app icons to `assets/` (icon.png, splash.png, adaptive-icon.png) before publishing.\n"}
`,
  };
}

export async function downloadFilesAsZip(fileMap, filename) {
  const zip = new JSZip();
  for (const [path, content] of Object.entries(fileMap)) {
    if (content && typeof content === "object" && content.__binary) {
      zip.file(path, content.data, { base64: true });
    } else {
      zip.file(path, content);
    }
  }
  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(blob, filename);
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
